/**
 * Validation module for Postpaid form
 */

import { validateQuantity } from '../../../../utils/quantityValidation';

// Validates if a store ID is valid
export const validateStoreId = (storeId) => {
  return storeId != null && String(storeId).trim() !== '';
};

// Validates if a material code is valid
export const validateMaterialCode = (materialCode, itemType) => {
  // MSISDN and VOBB don't require material codes
  if (itemType === 'MSISDN' || itemType === 'VOBB') {
    return true;
  }
  return materialCode != null && String(materialCode).trim() !== '';
};

// Validates if a region is valid
export const validateRegion = (region) => {
  return region != null && String(region).trim() !== '';
};

// Validates if an exchange area is valid
export const validateExchangeArea = (exchangeArea) => {
  return exchangeArea != null && String(exchangeArea).trim() !== '';
};

// Validates if a charge area is valid
export const validateChargeArea = (chargeArea) => {
  return chargeArea != null && String(chargeArea).trim() !== '';
};

// Helper function to safely get and trim a field value as string
const getFieldValue = (fields, fieldName) => {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return '';
  const val = fields[fieldName] ?? fields[`field_${fieldName}`];
  return typeof val === 'string' ? val.trim() : (val != null ? String(val).trim() : '');
};

// Validates if an item's fields are completely filled
export const isItemComplete = (fields, itemType) => {
  if (!fields) return false;

  // VOBB has different field structure
  if (itemType === 'VOBB') {
    const hasRegion = getFieldValue(fields, 'region');
    const hasExchangeArea = getFieldValue(fields, 'exchange_area');
    const hasChargeArea = getFieldValue(fields, 'charge_area');
    const hasQuantity = getFieldValue(fields, 'quantity');
    return hasRegion && hasExchangeArea && hasChargeArea && hasQuantity;
  }

  const hasStoreId = getFieldValue(fields, 'store_id');
  const hasQuantity = getFieldValue(fields, 'quantity');
  const hasMaterialCode = getFieldValue(fields, 'material_code');
  return hasStoreId && hasQuantity &&
         (itemType === 'MSISDN' || hasMaterialCode);
};

// Validates if Blank Sim data has any field filled
export const isBlankSimComplete = (blankSimData) => {
  if (!blankSimData) return false;
  const hasStoreId = getFieldValue(blankSimData, 'store_id');
  const hasMaterialCode = getFieldValue(blankSimData, 'material_code');
  const hasQuantity = getFieldValue(blankSimData, 'quantity');
  return hasStoreId || hasMaterialCode || hasQuantity;
};

// Validates if MSISDN data has any field filled
export const isMsisdnDataFilled = (msisdnData) => {
  if (!msisdnData) return false;
  
  return (
    getFieldValue(msisdnData, 'store_id') || 
    getFieldValue(msisdnData, 'quantity')
  );
};

// Validates if VOBB data has any field filled
export const isVobbDataFilled = (vobbData) => {
  if (!vobbData) return false;

  return (
    getFieldValue(vobbData, 'region') ||
    getFieldValue(vobbData, 'exchange_area') ||
    getFieldValue(vobbData, 'charge_area') ||
    getFieldValue(vobbData, 'quantity')
  );
};

// Validates if eSIM data has any field filled
export const isEsimDataFilled = (esimData) => {
  if (!esimData) return false;
  
  return (
    getFieldValue(esimData, 'store_id') || 
    getFieldValue(esimData, 'material_code') || 
    getFieldValue(esimData, 'quantity')
  );
};

// Validates the general form data
export const validateGeneralData = (generalData) => {
  const errors = {
    cr_project_name: '',
    validity_period: '',
    environment: ''
  };

  let isValid = true;

  if (!generalData?.cr_project_name?.trim()) {
    errors.cr_project_name = 'CR/Project Name is required *';
    isValid = false;
  }

  if (!generalData?.validity_period) {
    errors.validity_period = 'Validity Period is required *';
    isValid = false;
  }

  if (!generalData?.environment) {
    errors.environment = 'Environment is required *';
    isValid = false;
  }

  return { isValid, errors };
};

// Main form validation function
export const validatePostpaidForm = (formData) => {
  let isValid = true;
  const errors = {
    general: {
      cr_project_name: '',
      validity_period: '',
      environment: '',
      test_data: ''
    }
  };

  // Validate general data
  const generalValidation = validateGeneralData(formData.general);
  errors.general = { ...errors.general, ...generalValidation.errors };
  isValid = isValid && generalValidation.isValid;

  // Check if Blank Sim is fully filled
  const blankSimData = formData.Postpaid?.['Blank Sim'] || {};
  const isBlankSimFilled = isBlankSimComplete(blankSimData);

  // If Blank Sim is not filled, validate other test data items
  let hasValidTestData = false;

  // Validate existing form data
  Object.entries(formData).forEach(([section, items]) => {
    if (section !== 'general') {
      Object.entries(items).forEach(([item, fields]) => {
        // VOBB has different field structure
        if (item === 'VOBB') {
          const hasRegion = getFieldValue(fields, 'region');
          const hasExchangeArea = getFieldValue(fields, 'exchange_area');
          const hasChargeArea = getFieldValue(fields, 'charge_area');
          const hasQuantity = getFieldValue(fields, 'quantity');

          // Skip validation if no fields are filled for VOBB
          if (!hasRegion && !hasExchangeArea && !hasChargeArea && !hasQuantity) {
            return;
          }

          // Initialize error objects if needed
          if (!errors[section]) errors[section] = {};
          if (!errors[section][item]) errors[section][item] = {};

          // Validate VOBB fields
          if (!hasRegion) {
            errors[section][item].region = 'Region is required *';
            isValid = false;
          }

          if (!hasExchangeArea) {
            errors[section][item].exchange_area = 'Exchange Area is required *';
            isValid = false;
          }

          if (!hasChargeArea) {
            errors[section][item].charge_area = 'Charge Area is required *';
            isValid = false;
          }

          // Validate quantity
          const quantityError = validateQuantity(hasQuantity, 'postpaid', item);
          if (quantityError) {
            errors[section][item].quantity = quantityError;
            isValid = false;
          }

          // Check if this item is completely filled
          if (isItemComplete(fields, item)) {
            hasValidTestData = true;
          }
          return;
        }

        // Standard validation for non-VOBB items
        const hasStoreId = getFieldValue(fields, 'store_id');
        const hasQuantity = getFieldValue(fields, 'quantity');
        const hasMaterialCode = getFieldValue(fields, 'material_code');
        if (!hasStoreId && !hasQuantity && !hasMaterialCode) {
          return;
        }
        // Initialize error objects if needed
        if (!errors[section]) errors[section] = {};
        if (!errors[section][item]) errors[section][item] = {};
        // Validate store ID
        if (!validateStoreId(hasStoreId)) {
          errors[section][item].store_id = 'Store ID is required *';
          isValid = false;
        }
        // Validate quantity
        const quantityError = validateQuantity(hasQuantity, 'postpaid', item);
        if (quantityError) {
          errors[section][item].quantity = quantityError;
          isValid = false;
        }
        // Validate material code
        if (!validateMaterialCode(hasMaterialCode, item)) {
          errors[section][item].material_code = 'Material Code is required *';
          isValid = false;
        }
        // Check if this item is completely filled
        if (isItemComplete(fields, item)) {
          hasValidTestData = true;
        }
      });
    }
  });

  if (!isBlankSimFilled && !hasValidTestData) {
    errors.general.test_data = 'At least one test data item must be completely filled';
    isValid = false;
  }

  return { isValid, errors };
};

// Validate a single field change
export const validateFieldChange = (fieldName, value, itemType) => {
  let error = '';

  switch (fieldName) {
    case 'quantity':
      error = validateQuantity(value, 'postpaid', itemType);
      break;

    case 'store_id':
      if (!validateStoreId(value)) {
        error = 'This field is required *';
      }
      break;

    case 'material_code':
      if (!validateMaterialCode(value, itemType)) {
        error = 'This field is required *';
      }
      break;

    case 'region':
      if (!validateRegion(value)) {
        error = 'This field is required *';
      }
      break;

    case 'exchange_area':
      if (!validateExchangeArea(value)) {
        error = 'This field is required *';
      }
      break;

    case 'charge_area':
      if (!validateChargeArea(value)) {
        error = 'This field is required *';
      }
      break;
  }

  return error;
};